// Importing necessary libraries and components for the application.
import {
  ApolloClient,
  ApolloProvider,
  NormalizedCacheObject,
} from "@apollo/client";
import { useEffect, useState } from "react";
import { Toaster } from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { useRecoilState } from "recoil";
import AllRoutes from "./Admin/Allroutes/AllRoutes";
import usePathHistory from "./Admin/custom_hooks/usePathHistory";
import pathState from "./atoms/pathstate";
import { initializeClient } from "./CommonFunction/ApolloClient";
import { GetLink } from "./CommonFunction/GetLink";
import { getUserID } from "./CommonFunction/GetUserInfo";
import socket from "./CommonFunction/SocketClient";
import { getFirstSegment } from "./CommonFunction/common";

// ──────────────────────────────────────────────────────────────
// Extend window to support __customPageTitleSet globally
declare global {
  interface Window {
    __customPageTitleSet?: boolean;
  }
}

// Main App component.
function App() {
  const { toggle } = useSelector((store: any) => store?.ComponentPropReducer);
  const [Client, setClient] =
    useState<ApolloClient<NormalizedCacheObject> | null>(null);
  const { currentPath, previousPath, PerviousTitle, CurrentTitle } =
    usePathHistory();
  const { ComponentProp } = useSelector(
    (store: any) => store?.ComponentPropReducer
  );
  const dispatch = useDispatch();
  const [paths, setPaths] = useRecoilState(pathState);

  const rights = useSelector((state: any) => {
    return state.userRightsReducer.userRightsData[
      getFirstSegment(window.location.pathname)
    ];
  });

  const [UserID, setUserID] = useState("");

  const loginChange = sessionStorage.getItem("LicenseeCode");

  // Track path and title transitions
  useEffect(() => {
    setPaths({ currentPath, previousPath, PerviousTitle, CurrentTitle });
  }, [currentPath, previousPath, PerviousTitle, CurrentTitle]);

  // ───── Set global document.title if not overridden by page ─────
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!window.__customPageTitleSet) {
        if (rights?.Page_Name || window.location.pathname === "/") {
          document.title = rights?.Page_Name || "Headtler-ERP";
        }
      }
    }, 0);

    return () => clearTimeout(timeout);
  }, [window.location.pathname, rights]);

  // ───── Apollo Client setup ─────
  useEffect(() => {
    async function setup() {
      if (
        sessionStorage.getItem("LicenseeCode") &&
        sessionStorage.getItem("CompanyCode") &&
        sessionStorage.getItem("BranchCode")
      ) {
        const clientInstance = await initializeClient();
        setClient(clientInstance);
      } else {
        const defaultClient = GetLink();
        setClient(defaultClient);
      }
    }
    setup();
  }, [loginChange]);

  // ───── Fetch UserID ─────
  useEffect(() => {
    async function fetchUserID() {
      let info = await getUserID();
      if (info) {
        setUserID(info);
      }
    }
    fetchUserID();
  }, []);

  // ───── Global Hotkey Handler ─────
  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === "F1") {
      event.preventDefault();
      (document.querySelector(".globalfilterinputbox") as HTMLElement)?.focus();
    } else if (event.key === "F5") {
      event.preventDefault();
    }
  };

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  // ───── Back Button Modal Clear ─────
  useEffect(() => {
    const handleBackButton = () => {
      dispatch({ type: "CLEAR_ALL_COMPONENTS" });
    };
    window.addEventListener("popstate", handleBackButton);
    return () => {
      window.removeEventListener("popstate", handleBackButton);
    };
  }, []);

  if (!Client) {
    return null; // Or a loading spinner
  }

  return (
    <ApolloProvider client={Client}>
      <div className="App">
        <AllRoutes />
        {/* {toggle && <ModalComponent ComponentProp={ComponentProp} />} */}
      </div>
      <Toaster />
    </ApolloProvider>
  );
}

export default App;
